import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import { ExtraSmall } from '@ubeya/shared-web/components';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';

const ScheduleValue = styled(ExtraSmall)`
  color: ${({ theme }) => theme.colors.grey4};
  unicode-bidi: plaintext;
`;

const PositionRenderer = ({
  rowData,
  rowData: { isExternal, supplierData, scheduleIn, scheduleOut, scheduleTotal, comments },
  columnData: { mappedPositions, showDuration = true, showShiftHours = true, key = 'positionId' }
}) => {
  const positionId = rowData?.[key];
  const { timeFormat, t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();
  const scheduleDuration = useMemo(
    () => showDuration && scheduleIn && scheduleOut && formattingMap.number(scheduleTotal / 1000 / 60 / 60),
    [formattingMap, showDuration, scheduleIn, scheduleOut, scheduleTotal]
  );

  const { startTime, endTime } = useMemo(
    () => ({
      startTime:
        scheduleIn && moment(scheduleIn).isValid() ? moment(scheduleIn, API_DATE_TIME_FORMAT).format(timeFormat) : null,
      endTime:
        scheduleOut && moment(scheduleOut).isValid()
          ? moment(scheduleOut, API_DATE_TIME_FORMAT).format(timeFormat)
          : null
    }),
    [scheduleIn, scheduleOut, timeFormat]
  );

  const renderPositionWithHours =
    showShiftHours && startTime ? (endTime ? `${startTime} - ${endTime}` : `${startTime}`) : null;

  const position = useMemo(
    () => (positionId ? mappedPositions[positionId] : isExternal ? { label: t(supplierData?.positionSlug) } : null),
    [isExternal, mappedPositions, positionId, supplierData?.positionSlug, t]
  );

  return (
    <>
      {position && <>{position?.label}</>}
      {comments && (
        <>
          {position ? ', ' : ''}
          {comments}
        </>
      )}
      <ScheduleValue>
        {renderPositionWithHours}
        {scheduleDuration ? ` (${scheduleDuration}h)` : ''}
      </ScheduleValue>
    </>
  );
};

export default PositionRenderer;
